import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { ActionMode } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import React, { useMemo } from 'react'

export type BoxClickHandlerOptions = {
  selectedBoxIds: string[]
  setSelectedBoxIds: (ids: string[]) => void
  onBoxClickAction?: (box: Box, event: React.MouseEvent) => boolean
}

export const useBoxClickHandlers = ({
  selectedBoxIds,
  setSelectedBoxIds,
  onBoxClickAction,
}: BoxClickHandlerOptions) => {
  const boxClickHandlers: Record<
    ActionMode,
    (box: Box, event: React.MouseEvent) => void
  > = useMemo(
    () => ({
      [ActionMode.None]: () => {},
      [ActionMode.Assign]: (box: Box) => {
        assignBoxClickHandler(box, selectedBoxIds, setSelectedBoxIds)
      },
      [ActionMode.Remove]: (box: Box) => {
        removeBoxClickHandler(box, selectedBoxIds, setSelectedBoxIds)
      },
      [ActionMode.Group]: (box: Box) => {
        assignBoxClickHandler(box, selectedBoxIds, setSelectedBoxIds)
      },
      [ActionMode.Split]: (box: Box) => {
        assignBoxClickHandler(box, selectedBoxIds, setSelectedBoxIds)
      },
      [ActionMode.Info]: (box: Box, event: React.MouseEvent) => {
        infoBoxClickHandler(
          box,
          selectedBoxIds,
          setSelectedBoxIds,
          onBoxClickAction,
          event
        )
      },
      [ActionMode.Link]: (box: Box) => {
        assignBoxClickHandler(box, selectedBoxIds, setSelectedBoxIds)
      },
    }),
    [selectedBoxIds, setSelectedBoxIds, onBoxClickAction]
  )

  return boxClickHandlers
}

const assignBoxClickHandler = (
  box: Box,
  selectedBoxIds: string[],
  setSelectedBoxIds: (ids: string[]) => void
) => {
  if (box.confirmed) {
    return
  }
  if (!selectedBoxIds.includes(box.id)) {
    setSelectedBoxIds([...selectedBoxIds, box.id])
  } else if (selectedBoxIds.includes(box.id)) {
    setSelectedBoxIds(selectedBoxIds.filter((id) => id !== box.id))
  }
}

const removeBoxClickHandler = (
  box: Box,
  selectedBoxIds: string[],
  setSelectedBoxIds: (ids: string[]) => void
) => {
  if (!box.confirmed) {
    return
  }
  if (!selectedBoxIds.includes(box.id)) {
    setSelectedBoxIds([...selectedBoxIds, box.id])
  }
}

const infoBoxClickHandler = (
  box: Box,
  selectedBoxIds: string[],
  setSelectedBoxIds: (ids: string[]) => void,
  onBoxClickAction:
    | ((box: Box, event: React.MouseEvent) => boolean)
    | undefined,
  event: React.MouseEvent
) => {
  if (onBoxClickAction && onBoxClickAction(box, event)) {
    return
  }

  if (!selectedBoxIds.includes(box.id)) {
    setSelectedBoxIds([...selectedBoxIds, box.id])
  } else if (selectedBoxIds.includes(box.id)) {
    setSelectedBoxIds(selectedBoxIds.filter((id) => id !== box.id))
  }
}
